import { useCallback } from "react";

import { Box, Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { ShelfChangeStatus } from "__graphql__/types";
import { PageName, ShelfChangeAction, ShelfChangeProgressState } from "analytics/events";
import { routes } from "config/routes";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { Button } from "ui/Button/Button";
import { ArrowRightIcon, FacingsHuIcon } from "ui/Icons/Icons";
import { IconTextPill } from "ui/IconTextPill/IconTextPill";
import { NumberTextPill } from "ui/NumberTextPill/NumberTextPill";
import { ProductInfoBar } from "ui/ProductInfoBar/ProductInfoBar";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { Shelf } from "ui/Shelf/Shelf";
import { Status } from "ui/Status/Status";
import { getShelfDetail } from "utils/item";

type ShelfChangeDetailsCardProps = {
  proposalId: string;
  sku: string;
  shelfFrom: string | null;
  shelfTo: string | null;
  status: ShelfChangeStatus;
  name: string | null;
  imageUrl: string | null;
  numberOfShelfFacings: number | null;
  isShelvedInHandlingUnits: boolean | null;
  inventoryEntry: {
    stock?: {
      shelf: number;
    } | null;
  };
};

export function ShelfChangeDetailsCard({
  proposalId,
  sku,
  shelfFrom,
  shelfTo,
  status,
  name,
  imageUrl,
  numberOfShelfFacings,
  isShelvedInHandlingUnits,
  inventoryEntry,
}: ShelfChangeDetailsCardProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { sendSegmentTrackEvent } = useAnalytics();

  const navigateToStockCorrection = useCallback(() => {
    sendSegmentTrackEvent("shelfChangeStateUpdated", {
      state: ShelfChangeProgressState.AdjustStock,
      action: ShelfChangeAction.Click,
      shelf_change_id: proposalId,
      product_sku: sku ?? "",
      product_name: name ?? "",
      from_shelf_id: shelfFrom ?? "",
      to_shelf_id: shelfTo ?? "",
    });
    navigate(routes.inventory.stockCorrections.declare, {
      state: {
        sku,
        origin: PageName.INVENTORY_SHELF_CHANGES_PAGE,
        proposalId,
      },
    });
  }, [name, navigate, proposalId, sendSegmentTrackEvent, shelfFrom, shelfTo, sku]);

  const facingsLabel = isShelvedInHandlingUnits ? "Handling Units" : "Facings";

  const splittedShelfFrom = getShelfDetail(shelfFrom ?? "");
  const splittedShelfTo = getShelfDetail(shelfTo ?? "");

  return (
    <Box p="s200" bg="white" w="100%" data-testid="shelf-change-details-card">
      <Flex mb="s200" justifyContent="space-between" alignItems="center">
        {status === ShelfChangeStatus.completed ? (
          <Status
            label={intl.formatMessage({
              id: "pages.shelfChanges.shelfChangeDetailsCard.status.completed",
            })}
            bgColor="veggie.500"
            textColor="white"
          />
        ) : (
          <>
            <Shelf letter={splittedShelfFrom.shelfLetter} number={splittedShelfFrom.shelfNumber} />
            <ArrowRightIcon color="black" boxSize="24px" />
          </>
        )}
        <Shelf letter={splittedShelfTo.shelfLetter} number={splittedShelfTo.shelfNumber} />
      </Flex>
      <ProductInformation
        bg="grey.100"
        mb="s100"
        name={name ?? "Unknown Product"}
        imageUrl={imageUrl ?? undefined}
        stock={inventoryEntry.stock?.shelf ?? 0}
        variantSize="sm"
        showSKU={false}
        showLeftSideBar={status === ShelfChangeStatus.completed}
        leftSideBarProps={{ bgColor: "veggie.500" }}
      />
      <ProductInfoBar
        mb={status === ShelfChangeStatus.open ? "s200" : undefined}
        leftSide={<IconTextPill color="orange.500" icon={<FacingsHuIcon />} text={facingsLabel} />}
        rightSide={
          <NumberTextPill
            number={numberOfShelfFacings ?? 0}
            text={facingsLabel}
            color="orange.500"
          />
        }
      />
      {status === ShelfChangeStatus.open && (
        <Button onClick={navigateToStockCorrection} variant="secondary" isFullWidth>
          {intl.formatMessage({
            id: "pages.shelfChanges.shelfChangeDetailsCard.cta-button",
          })}
        </Button>
      )}
    </Box>
  );
}
